import * as moment from "moment";

import { CaseType, LinkObject } from "@teamprevent/common/model";
import { AppointmentModel } from "@teamprevent/common/appointment";
import { OhCaseBaseModel } from "@teamprevent/common/ohCase/model";
import { HrRepresentative } from "@teamprevent/common/hrRepresentatives/models";
import { LineManager } from "@teamprevent/common/lineManagers/models";
import { SecondaryCaseStatus } from "@teamprevent/common/workAdjustment/models";

import { SecondaryCaseStatusChangeEntry } from "./components/details/workAdjustment/models";

import { Location } from "redux-little-router";

export interface OhCaseListModel extends OhCaseBaseModel {
    readonly id: string;
    readonly caseId?: string;
    readonly employeeRecordId?: string;
    readonly matchedReferralId?: string;
    readonly employeeFirstName?: string;
    readonly employeeLastName?: string;
    readonly employeeSecondaryReference?: string;
    readonly lastUpdated?: moment.Moment;
    readonly caseType?: string;
    readonly caseStatus?: string;
    readonly listUpdated?: moment.Moment;
    readonly orgStructureOneName?: string;
    readonly secondaryCaseStatus?: SecondaryCaseStatus;
    readonly secondaryCaseStatusChangesNotes?: SecondaryCaseStatusChangeEntry[];
    readonly localDueDate?: moment.Moment;
}

export interface OhCaseDocument {
    id: string;
    name: string;
    documentType: string;
}

export interface OhCaseDetailModel {
    readonly id: string;
    readonly caseId?: string;
    readonly employeeRecordId?: string;
    readonly employeeFirstName?: string;
    readonly employeeLastName?: string;
    readonly matchedReferralId?: string;
    readonly referralSubmittedDate?: moment.Moment;
    readonly lastUpdated?: moment.Moment;
    readonly created?: moment.Moment;
    readonly caseType?: string;
    readonly caseStatus?: string;
    readonly detailUpdated?: moment.Moment;
    readonly dateOfBirth?: moment.Moment;
    readonly jobTitle?: string;
    readonly tasks?: TaskModel[];
    readonly associatedHrIds?: string[];
    readonly associatedManagerIds?: string[];
    readonly documents?: OhCaseDocument[];
    readonly appointments?: AppointmentModel[];
    readonly linkedOhCasesIds?: string[];
    readonly linkedOhCases?: ({ id: string; caseType: CaseType; caseId: number })[];
    readonly secondaryCaseStatus?: SecondaryCaseStatus;
    readonly secondaryCaseStatusChangesNotes?: SecondaryCaseStatusChangeEntry[];
    readonly physiotherapyReferralOrigin?: string;
    readonly lineManagers?: LineManager[];
    readonly hrRepresentatives?: HrRepresentative[];
    readonly localDueDate?: moment.Moment;
    readonly genericCaseType?: LinkObject;
}

export interface LoadOhCaseDocumentsPayload {
    ohCaseId: string;
    documents: OhCaseDocument[];
}

export interface LoadOhCaseAppointmentsPayload {
    ohCaseId: string;
    appointments: AppointmentModel[];
}

export interface TaskModel {
    readonly id: string;
    readonly dateCreated: moment.Moment;
    readonly createdBy: LinkObject;
    readonly caseId: string;
    readonly completed: boolean;
    readonly completedBy: LinkObject;
    readonly completedDate: moment.Moment;
    readonly type: string;
    readonly typeId: string;
    readonly notes: string;
    readonly dueDate: moment.Moment;
    readonly description: string;
    readonly cancelled: boolean;
    readonly didNotAttend?: boolean;
    readonly assignedUsers: LinkObject[];
    readonly customer: LinkObject;
    readonly employeeRecord: LinkObject;
    readonly appointmentId?: string;
    readonly precedingTaskId?: string;
    readonly caseSubject?: string;
}

export type OhCase = OhCaseListModel & OhCaseDetailModel;

export interface OhCaseCreateModel {
    id: string;
    employeeFirstName: string;
    employeeLastName: string;
    caseType: string;
    caseStatus: string;
    matchedReferralId: string;
    caseId: string;
    employeeRecordId: string;
}

export interface OhCaseEditModel {
    id: string;
    employeeFirstName: string;
    employeeLastName: string;
    lastUpdated: moment.Moment;
    caseType: string;
    caseStatus: string;
    caseId: string;
    matchedReferralId: string;
    employeeRecordId: string;
}

export interface OhCaseState {
    readonly ohCases: OhCase[];
    readonly ohCase?: OhCase;
}

export const ALL_CASE_STATUSES = "All";
export const OPEN_CASE_STATUS = "Open";
export const CLOSED_CASE_STATUS = "Closed";
export const CANCELLED_CASE_STATUS = "Cancelled";
export const RECALL_CASE_STATUS = "Recall";

export const CASE_STATUSES = [
    OPEN_CASE_STATUS,
    CLOSED_CASE_STATUS,
    CANCELLED_CASE_STATUS,
    RECALL_CASE_STATUS
];

export interface OhCaseTimelineEvent {
    completed?: boolean;
    completedDate?: moment.Moment;
    dateCreated?: moment.Moment;
    cancelled?: boolean;
}

const isCompletedOrCancelled = (task: OhCaseTimelineEvent) => task.completed || task.cancelled;

export const sortOhCaseTimeline = <T extends OhCaseTimelineEvent>(events: T[]) => {
    return events.sort(Sort);
};

export type AppState = OhCaseState & { router: Location };

export const parseModel = (model: OhCaseDetailModel): OhCaseDetailModel => {
    return {
        ...model,
        localDueDate: model.localDueDate && moment(model.localDueDate),
        lastUpdated: model.lastUpdated && moment(model.lastUpdated),
        created: model.created && moment(model.created),
        dateOfBirth: model.dateOfBirth && moment(model.dateOfBirth),
        caseId: model.caseId?.toString(),
        tasks: model.tasks && model.tasks.map(task => ({
            ...task,
            dateCreated: task.dateCreated && moment(task.dateCreated),
            completedDate: task.completedDate && moment(task.completedDate),
            dueDate: task.dueDate && moment(task.dueDate)
        }))
    };
};

const Sort = <T extends OhCaseTimelineEvent>(a: T, b: T) => {
    if (isCompletedOrCancelled(a) && !isCompletedOrCancelled(b)) {
        return 1;
    }

    if (!isCompletedOrCancelled(a) && isCompletedOrCancelled(b)) {
        return -1;
    }

    const aDate = (isCompletedOrCancelled(a) && a.completedDate) ? a.completedDate : a.dateCreated;
    const bDate = (isCompletedOrCancelled(b) && b.completedDate) ? b.completedDate : b.dateCreated;

    return -aDate.diff(bDate);
};
